import OmeSender from './OmeSender';
import OmeReceiver from './OmeReceiver';
import LiveKitSender from './liveKit/LiveKitSender';
import LiveKitRoomReceiver from './liveKit/LiveKitRoomReceiver';
import FetchPoller from './FetchPoller';

// How long a stream nobody is watching stays connected, for a scene switch to take it over.
const RECEIVER_LINGER_MS = 8000;

// Same URL normalization UserCall.vue's sendUrl/recvUrl computeds use: pick the secure variant over
// https, and make sure it actually carries a ws(s):// scheme.
function pickUrl(webRtcSettings, kind, wsHostOverride) {
	const secure = location.protocol === 'https:';
	let url = secure ? webRtcSettings?.[`${kind}Secure`] : webRtcSettings?.[kind];
	if(!url) return url;
	// The render container can need a different host[:port] than a real caller's browser does (a hostname
	// that's reachable for callers can still be firewalled off from it) - replace the whole host segment,
	// keeping only the scheme if there was one.
	if(wsHostOverride) url = url.replace(/^(wss?:\/\/)?[^/]+/, (_, scheme) => `${scheme || ''}${wsHostOverride}`);
	return /^wss?:\/\//.test(url) ? url : (secure ? 'wss://' : 'ws://') + url;
}

// Joining a call, publishing the local stream, polling for other active callers and opening
// receive-only connections to them - the WebRTC plumbing shared by the studio editor and (later)
// the guest page and the headless render page. Framework-independent, same style as OmeSender/
// OmeReceiver/FetchPoller themselves.
export default class CallSession {
	// includeSelf: keep this session's own caller in the list too. The studio's guest strip wants only the others, but a
	// guest's page renders the same scene the broadcast does, and on stage they're a tile in it like anyone else.
	constructor({ api, callId, webRtcSettings, wsHostOverride, includeSelf = false }) {
		this.api = api;
		this.callId = callId;
		this._includeSelf = includeSelf;
		this._serviceType = webRtcSettings?.type;
		this._liveKitUrl = webRtcSettings?.type === 'livekit' ? webRtcSettings.url : null;
		this._liveKitRoom = null;
		this.sendUrl = pickUrl(webRtcSettings, 'sendUrl', wsHostOverride);
		this.recvUrl = pickUrl(webRtcSettings, 'recvUrl', wsHostOverride);
		this.caller = null;
		this.stream = null;
		this.callers = []; // active callers other than this session's own - see onCallers()
		this._sender = null;
		this._callersPoller = null;
		this._callersListeners = new Set();
		this._leaving = false;
		this._screenSender = null;
		this._screenId = null;
		this._devices = new Map(); // extra camera stream id -> { sender, stream, label }
		this.ownScreenId = null;
		this._ownScreenListeners = new Set();
		this._streams = new Map(); // stream id -> shared receive connection (see openReceiver)
	}

	get ownCallerId() { return this.caller?.caller_id || null; }
	get screenId() { return this._screenId; }

	// Tell everyone whether our own mic is muted (shown on our tile).
	setMicMuted(muted) {
		if(!this.caller) return Promise.resolve();
		return this.api.post(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}/mic`, { muted }).catch(() => {});
	}

	// isHost: only the call owner's own studio session should ever pass this - it hits a separate,
	// authed API route that checks call ownership before setting is_host (see api/calls.js).
	// guestId: the guest's own browser-kept id, so a returning guest gets the same caller id back (see
	// StorageManager.createCallerForCall). Never sent for the host.
	async join({ name, stream, isHost = false, guestId = null }) {
		if(!this.sendUrl && !this._liveKitUrl) throw new Error(`This call's service${this._serviceType ? ` (${this._serviceType})` : ''} isn't supported by the call page`);
		this.stream = stream;
		this._joinPath = isHost ? `call/${encodeURIComponent(this.callId)}/host-caller` : `call/${encodeURIComponent(this.callId)}/caller`;
		this._joinBody = { name, ...(guestId && !isHost ? { guestId } : {}) };
		this.caller = await this.api.post(this._joinPath, this._joinBody);
		this._startSender();
		this._startCallersPoller();
		return this.caller;
	}

	// Watch the call without being in it - the headless render page never publishes anything of its own.
	spectate() {
		this._startCallersPoller();
	}

	leave() {
		this._leaving = true;
		this._callersPoller?.stop();
		this._callersPoller = null;
		this._sender?.stop();
		this._sender = null;
		this._screenSender?.stop();
		this._screenSender = null;
		for(const id of [ ...this._devices.keys() ]) this._stopDeviceSender(id);
		for(const shared of [ ...this._streams.values() ]) this._dropStream(shared);
		this._liveKitRoom?.close();
		this.callers = [];
		if(this.caller) this.api.delete(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}`).catch(() => {});
		this.caller = null;
	}

	// The screen share is published as a second, independent OME stream, so starting or stopping one
	// is never mistaken for leaving the call - same reasoning as UserCall.vue's screen share.
	async startScreenShare(stream) {
		if(!this.caller) return;
		this._screenSender?.stop();
		this._screenSender = null;
		const { screen_id } = await this.api.post(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}/screen`);
		this._screenId = screen_id;
		this._screenSender = this._createSender(screen_id, stream, {
			screen: true,
			onClose: () => { if(this._screenSender && !this._leaving) setTimeout(() => this.startScreenShare(stream), 2000); },
		});
		this._screenSender.start();
		return screen_id;
	}

	stopScreenShare() {
		const screenId = this._screenId;
		this._screenSender?.stop();
		this._screenSender = null;
		this._screenId = null;
		if(screenId && this.caller) this.api.delete(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}/screen`).catch(() => {});
	}

	// An extra camera, published as its own OME stream next to the main one - like the screen share, so adding
	// or removing one is never mistaken for leaving the call. Returns the new stream id.
	async startDevice(stream, label, key) {
		if(!this.caller) return null;
		const { id } = await this.api.post(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}/device`, { label, key });
		const sender = this._createSender(id, stream, {
			onClose: () => { if(this._devices.get(id)?.sender === sender && !this._leaving) setTimeout(() => this._restartDevice(id), 2000); },
		});
		this._devices.set(id, { sender, stream, label });
		sender.start();
		return id;
	}

	stopDevice(id) {
		if(!this._devices.has(id)) return;
		this._stopDeviceSender(id);
		if(this.caller) this.api.delete(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}/device/${encodeURIComponent(id)}`).catch(() => {});
	}

	// Own extra cameras, for the tiles that show them locally.
	get devices() {
		return [ ...this._devices ].map(([ id, d ]) => ({ id, label: d.label, stream: d.stream }));
	}

	_stopDeviceSender(id) {
		this._devices.get(id)?.sender.stop();
		this._devices.delete(id);
	}

	_restartDevice(id) {
		const d = this._devices.get(id);
		if(!d || this._leaving) return;
		d.sender = this._createSender(id, d.stream, {
			onClose: () => { if(this._devices.get(id)?.sender === d.sender && !this._leaving) setTimeout(() => this._restartDevice(id), 2000); },
		});
		d.sender.start();
	}

	_startSender() {
		this._sender?.stop();
		this._sender = this._createSender(this.caller.caller_id, this.stream, {
			onClose: () => { if(this.caller && !this._leaving) setTimeout(() => this._reconnectSender(), 2000); },
		});
		this._sender.start();
	}

	// One published stream: OME takes it by URL, LiveKit by a publish token for the stream id.
	_createSender(streamId, stream, { screen = false, onClose } = {}) {
		if(this._liveKitUrl) {
			const getToken = () => this.api.post(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}/publishToken`, { streamId }).then(r => r.token);
			return new LiveKitSender(this._liveKitUrl, getToken, stream, { screen, onClose });
		}
		return new OmeSender(this.sendUrl.replaceAll('{caller_id}', streamId), stream, { onClose });
	}

	// The server only lets a stream name publish while its caller row exists. If the row was cleaned up
	// while we were offline for a while, retrying the old name is rejected forever - join again instead.
	async _reconnectSender() {
		if(!this.caller || this._leaving) return;
		try {
			await this.api.get(`call/${encodeURIComponent(this.callId)}/caller/${encodeURIComponent(this.caller.caller_id)}`);
		} catch(e) {
			if(e.message === 'Not found') {
				try {
					this.caller = await this.api.post(this._joinPath, this._joinBody);
				} catch(joinError) {
					console.error('Could not rejoin call', joinError);
				}
			}
		}
		if(this.caller && !this._leaving) this._startSender();
	}

	// Swaps the published tracks (device/mute change) without dropping the publish session.
	async replaceStream(stream) {
		this.stream = stream;
		if(!this._sender || !await this._sender.replaceStream(stream)) this._startSender();
	}

	_startCallersPoller() {
		this._callersPoller = new FetchPoller(async () => {
			const list = await this.api.get(`call/${encodeURIComponent(this.callId)}/callers`);
			const me = list.find(c => c.caller_id === this.ownCallerId);
			const ownScreenId = me?.screen_status === 'active' ? me.screen_id : null;
			if(ownScreenId !== this.ownScreenId) {
				this.ownScreenId = ownScreenId;
				for(const cb of this._ownScreenListeners) cb(ownScreenId);
			}
			this.callers = list
				.filter(c => this._includeSelf || c.caller_id !== this.ownCallerId)
				.map(c => ({
					callerId: c.caller_id,
					screenId: c.screen_status === 'active' ? c.screen_id : null,
					name: c.name,
					isOwner: !!c.is_host,
					onStage: !!c.on_stage,
					micMuted: !!c.mic_muted,
					devices: c.devices || [],
				}));
			this._notifyCallers();
		}, 1000);
		this._callersPoller.start();
	}

	// This session's own screen share, when it's live (the caller list leaves a session's own row out).
	onOwnScreen(cb) {
		this._ownScreenListeners.add(cb);
		cb(this.ownScreenId);
		return () => this._ownScreenListeners.delete(cb);
	}

	// cb is called immediately with the current list, then again on every change; returns an unsubscribe.
	onCallers(cb) {
		this._callersListeners.add(cb);
		cb(this.callers);
		return () => this._callersListeners.delete(cb);
	}

	// The call owner's stream, as seen from this session: cb gets the received stream (or null while there
	// isn't one). Follows whichever caller is the host, reconnecting only when that caller id changes rather
	// than on every poll tick. A session that IS the owner has no use for this - it already holds the stream.
	onOwnerStream(cb) {
		let receiver = null;
		let currentOwnerId = null;
		const connect = ownerId => {
			receiver?.close();
			receiver = null;
			if(!ownerId) { cb(null); return; }
			receiver = this.openReceiver(ownerId);
			receiver.onStream(stream => cb(stream));
		};
		const unsubscribe = this.onCallers(callers => {
			const ownerId = callers.find(c => c.isOwner)?.callerId || null;
			if(ownerId !== currentOwnerId) { currentOwnerId = ownerId; connect(ownerId); }
		});
		return () => { unsubscribe(); receiver?.close(); };
	}

	_notifyCallers() {
		for(const cb of this._callersListeners) cb(this.callers);
	}

	// A receive-only handle on one participant's stream - their camera by default, their screen share or an extra
	// device when deviceId is given. Every handle on the same stream shares one connection (the grid, a speaker layer
	// and the audio all watching the same person), and a stream nobody holds any more is kept for RECEIVER_LINGER_MS
	// before it's closed: switching scenes destroys one scene's layers and builds the next one's, which would
	// otherwise drop every picture and reconnect it a moment later.
	openReceiver(callerId, deviceId) {
		const streamId = deviceId || callerId;
		let shared = this._streams.get(streamId);
		if(!shared) {
			shared = { streamId, receiver: null, lastStream: null, listeners: new Set(), refs: 0, closeTimer: null, closed: false };
			this._streams.set(streamId, shared);
			this._connectStream(shared);
		}
		clearTimeout(shared.closeTimer);
		shared.refs++;
		let onStreamCb = null;
		let released = false;
		const listener = stream => onStreamCb?.(stream);
		shared.listeners.add(listener);
		return {
			onStream(cb) { onStreamCb = cb; if(shared.lastStream) cb(shared.lastStream); },
			close: () => {
				if(released) return;
				released = true;
				shared.listeners.delete(listener);
				if(--shared.refs > 0) return;
				shared.closeTimer = setTimeout(() => this._dropStream(shared), RECEIVER_LINGER_MS);
			},
		};
	}

	_connectStream(shared) {
		if(shared.closed || !(this.recvUrl || this._liveKitUrl)) return;
		const emit = stream => {
			shared.lastStream = stream;
			for(const listener of shared.listeners) listener(stream);
		};
		shared.receiver = this._createReceiver(shared.streamId, {
			onStream: emit,
			onClose: () => { if(shared.closed) return; emit(null); setTimeout(() => this._connectStream(shared), 2000); },
		});
		shared.receiver.start();
	}

	// OME opens a connection per stream; LiveKit receives every stream over one connection per page.
	_createReceiver(streamId, handlers) {
		if(this._liveKitUrl) {
			if(!this._liveKitRoom) this._liveKitRoom = new LiveKitRoomReceiver(this._liveKitUrl, () => this.api.post(`call/${encodeURIComponent(this.callId)}/subscribeToken`).then(r => r.token));
			return this._liveKitRoom.openStream(streamId, handlers);
		}
		return new OmeReceiver(this.recvUrl.replaceAll('{caller_id}', streamId), handlers);
	}

	_dropStream(shared) {
		shared.closed = true;
		clearTimeout(shared.closeTimer);
		shared.receiver?.stop();
		shared.receiver = null;
		this._streams.delete(shared.streamId);
	}
}
